import { checksum } from './checksum';
import { SeededRng, shuffle } from './rng';
import type {
  ArenaDefinition,
  BattleCommand,
  BattleEvent,
  BattleRules,
  BattleState,
  BattleView,
  CardDefinition,
  CardId,
  CommandRejectReason,
  CommandResult,
  ContentSnapshot,
  DeepReadonly,
  EntityId,
  EntityState,
  ElixirStage,
  GroundRouteState,
  PlayerId,
  ProjectileState,
  SpawnDefinition,
  TargetMask,
  WorldPoint,
} from './types';
import { clampPoint, projectileOrigin } from './arena-runtime';
import { createGroundRouteState, type NavigationPlan, routePlan } from './navigation';
import { constrainGroundStep, type MovementResolution } from './navigation/constraints';
import { activeDeploymentZones, deploymentZoneForPoint, isCardPlacementValid, planSpawnDefinitions, planSpawnGroups, resolveRuntimeSpawnPosition } from './placement';
import { entityAnimationState } from './animation-state';
import { acquireTarget, isValidTarget } from './targeting';
import { SpatialIndex } from './spatial-index';
import { computeFriendlyGroundSeparation, SEPARATION_BUDGET } from './separation';
import { DiagnosticJournal, type DiagnosticProfile } from '../diagnostics/journal';
import { createReplay, type BattleSnapshot, type BotStrategyProvenance, type ReplayFile } from '../replay/replay';
import { DECK_SIZE, isValidDeckComposition } from './deck-rules';
import { cardCooldownAt, elixirRateAt, elixirStageAt } from './battle-rules';

export const ENGINE_VERSION = '1.5.0-navigation.1';
/** Internal test/benchmark hook. Production consumers must use state/getView. */
export const ENGINE_TEST_ACCESS: unique symbol = Symbol('ENGINE_TEST_ACCESS');
export const DEFAULT_RULES: BattleRules = {
  id: 'towerarena-1v1',
  tickRate: 30,
  countdownTicks: 90,
  normalTicks: 5_400,
  overtimeTicks: 3_600,
  startingElixirMilli: 6_000,
  maxElixirMilli: 10_000,
  singleElixirPerTickMilli: 12,
  doubleElixirPerTickMilli: 24,
  tripleElixirPerTickMilli: 36,
  normalDoubleElixirStartTick: 3_600,
  overtimeTripleElixirStartTick: 1_800,
  singleCardCooldownTicks: 30,
  doubleCardCooldownTicks: 15,
  tripleCardCooldownTicks: 11,
};

const otherPlayer = (playerId: PlayerId): PlayerId => (playerId === 'player' ? 'bot' : 'player');
const kingSocket = (playerId: PlayerId) => playerId === 'player' ? 'playerKing' as const : 'botKing' as const;
const distance = (left: WorldPoint, right: WorldPoint): number => Math.hypot(left.x - right.x, left.y - right.y);
const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
};

const cloneContent = (content: ContentSnapshot): ContentSnapshot => ({
  version: content.version,
  cards: new Map([...content.cards].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
  units: new Map([...content.units].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
  spells: new Map([...content.spells].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
  arenas: new Map([...content.arenas].map(([id, definition]) => [id, deepFreeze(structuredClone(definition))])),
});

export interface EngineOptions {
  seed: number;
  content: ContentSnapshot;
  arenaId: string;
  decks: Readonly<Record<PlayerId, readonly CardId[]>>;
  rules?: BattleRules;
  diagnosticProfile?: DiagnosticProfile;
  matchId?: string;
  botStrategy?: BotStrategyProvenance;
}

export class BattleEngine {
  readonly version = ENGINE_VERSION;
  readonly journal: DiagnosticJournal;
  readonly replay: ReplayFile;
  readonly checkpoints: BattleSnapshot[] = [];
  private readonly _state: BattleState;
  private readonly content: ContentSnapshot;
  private readonly rng: SeededRng;
  private readonly arena: ArenaDefinition;
  private readonly rules: BattleRules;
  private readonly commands: BattleCommand[] = [];
  private readonly events: BattleEvent[] = [];
  private readonly lastStateSamples = new Map<EntityId, string>();
  private spatialIndex: SpatialIndex<EntityState> | null = null;
  private lastElixirStage: ElixirStage | null = null;
  private stateRevision = 0;
  private publicStateCache: { revision: number; value: DeepReadonly<BattleState> } | null = null;

  constructor(options: EngineOptions) {
    this.content = cloneContent(options.content);
    this.rules = structuredClone(options.rules ?? DEFAULT_RULES);
    this.rng = new SeededRng(options.seed);
    const arena = [...this.content.arenas.values()].find((item) => item.id === options.arenaId);
    if (!arena) throw new Error(`Unknown arena: ${options.arenaId}`);
    this.arena = arena;
    for (const playerId of ['player', 'bot'] as const) {
      const deck = options.decks[playerId];
      if (!isValidDeckComposition(deck)) throw new Error(`${playerId} deck must contain exactly ${DECK_SIZE} unique cards`);
      for (const cardId of options.decks[playerId]) if (!this.content.cards.has(cardId)) throw new Error(`Unknown card in deck: ${cardId}`);
    }
    const playerDeck = shuffle(options.decks.player, this.rng);
    const botDeck = shuffle(options.decks.bot, this.rng);
    this._state = {
      version: 1,
      tick: 0,
      seed: options.seed >>> 0,
      arenaId: arena.id,
      phase: 'countdown',
      phaseTick: 0,
      nextEntityId: 1,
      players: {
        player: { id: 'player', elixirMilli: this.rules.startingElixirMilli, nextCardReadyTick: 0, hand: playerDeck.slice(0, 4), queue: playerDeck.slice(4), crowns: 0, nextSequence: 1 },
        bot: { id: 'bot', elixirMilli: this.rules.startingElixirMilli, nextCardReadyTick: 0, hand: botDeck.slice(0, 4), queue: botDeck.slice(4), crowns: 0, nextSequence: 1 },
      },
      entities: [],
      projectiles: [],
      result: null,
    };
    this.journal = new DiagnosticJournal(options.matchId ?? `match-${options.seed}`, options.diagnosticProfile ?? 'full');
    this.replay = createReplay(options.seed, ENGINE_VERSION, this.content.version, arena.id, this.rules, options.decks, options.botStrategy);
    this.spawnInitialTowers();
    this.record('simulation', 'MATCH_CREATED', { seed: options.seed, arenaId: arena.id, decks: options.decks, geometry: arena.gameplay, botStrategy: options.botStrategy ?? null });
    this.checkpoint();
  }

  get state(): DeepReadonly<BattleState> {
    if (this.publicStateCache?.revision === this.stateRevision) return this.publicStateCache.value;
    const value = deepFreeze(structuredClone(this._state)) as DeepReadonly<BattleState>;
    this.publicStateCache = { revision: this.stateRevision, value };
    return value;
  }

  [ENGINE_TEST_ACCESS](): BattleState {
    this.stateRevision += 1;
    this.publicStateCache = null;
    return this._state;
  }

  get arenaDefinition(): DeepReadonly<ArenaDefinition> {
    return this.arena;
  }

  get currentTick(): number {
    return this._state.tick;
  }

  get hasEnded(): boolean {
    return this._state.result !== null;
  }

  canPlaceCard(playerId: PlayerId, cardId: CardId, position: WorldPoint): boolean {
    const card = this.content.cards.get(cardId);
    return Boolean(card && this.isPlacementValid(playerId, card, position));
  }

  nearestValidPlacement(playerId: PlayerId, cardId: CardId, position: WorldPoint, maxDistance = 900): WorldPoint | null {
    const card = this.content.cards.get(cardId);
    if (!card) return null;
    if (this.isPlacementValid(playerId, card, position)) return { ...position };
    if (card.placement === 'anywhere') return null;
    const candidates = activeDeploymentZones(this.arena.gameplay, playerId, this._state.entities)
      .map((zone) => ({
        x: Math.max(zone.bounds.x + 1, Math.min(zone.bounds.x + zone.bounds.width - 1, position.x)),
        y: Math.max(zone.bounds.y + 1, Math.min(zone.bounds.y + zone.bounds.height - 1, position.y)),
      }))
      .filter((candidate) => distance(candidate, position) <= maxDistance && this.isPlacementValid(playerId, card, candidate))
      .sort((left, right) => distance(left, position) - distance(right, position) || left.x - right.x || left.y - right.y);
    return candidates[0] ?? null;
  }

  enqueue(command: BattleCommand): CommandResult {
    const reason = this.validateCommand(command);
    if (reason) {
      const event: BattleEvent = { type: 'command-rejected', tick: this._state.tick, command: structuredClone(command), reason };
      this.events.push(event);
      this.record('command', 'COMMAND_REJECTED', { reason, command }, { commandSequence: command.sequence, cardIds: [command.cardId], level: 'warn' });
      return { ok: false, reason };
    }
    this.commands.push(structuredClone(command));
    this.commands.sort((left, right) => left.tick - right.tick || left.playerId.localeCompare(right.playerId) || left.sequence - right.sequence);
    this._state.players[command.playerId].nextSequence += 1;
    this.stateRevision += 1;
    this.replay.commands.push(structuredClone(command));
    this.events.push({ type: 'command-accepted', tick: this._state.tick, command: structuredClone(command) });
    this.record('command', 'COMMAND_ACCEPTED', { command }, { commandSequence: command.sequence, cardIds: [command.cardId] });
    return { ok: true, acceptedTick: command.tick };
  }

  step(): readonly BattleEvent[] {
    if (this._state.result) return [];
    const start = performance.now();
    this.events.length = 0;
    this.applyDueCommands();
    this.updateClockAndElixir();
    if (!this._state.result) {
      this.updateEntities();
      this.resolveSeparation();
      this.updateProjectiles();
      this.resolveDeathsAndResult();
    }
    this.cleanup();
    if (this._state.result) {
      this.checkpoint();
    } else {
      this._state.tick += 1;
      this._state.phaseTick += 1;
      if (this._state.tick % 150 === 0) this.checkpoint();
      if (this._state.tick % 30 === 0) this.recordStateSamples();
    }
    const elapsed = performance.now() - start;
    this.stateRevision += 1;
    const budgetMs = 1_000 / this.rules.tickRate;
    if (elapsed > budgetMs) this.record('performance', 'TICK_OVERRUN', { elapsedMs: elapsed, budgetMs }, { level: 'warn' });
    else if (elapsed > 4) this.record('performance', 'TICK_SLOW', { elapsedMs: elapsed, budgetMs }, { level: 'debug' });
    return [...this.events];
  }

  run(ticks: number): void {
    for (let index = 0; index < ticks && !this._state.result; index += 1) this.step();
  }

  getView(viewer: PlayerId): BattleView {
    const opponent = this._state.players[otherPlayer(viewer)];
    return {
      tick: this._state.tick,
      phase: this._state.phase,
      arena: this.arena.gameplay,
      deploymentZones: structuredClone(activeDeploymentZones(this.arena.gameplay, viewer, this._state.entities)),
      player: structuredClone(this._state.players[viewer]),
      opponent: { id: opponent.id, crowns: opponent.crowns },
      entities: this._state.entities.filter((entity) => !entity.removed).map((entity) => {
        const target = this.entityById(entity.targetId);
        const animation = entityAnimationState(entity, target);
        return {
        id: entity.id,
        owner: entity.owner,
        kind: entity.kind,
        cardId: entity.sourceCardId,
        x: entity.position.x,
	        y: entity.position.y,
	        hp: entity.hp,
	        maxHp: entity.maxHp,
	        radius: entity.radius,
	        movement: entity.movement,
	        moving: animation.moving,
        attacking: animation.attacking,
        leaping: entity.leapState !== null,
        deploying: entity.deployRemaining > 0,
        active: entity.active,
        dead: entity.dead,
        targetId: entity.targetId,
        visualKey: entity.visualKey,
        };
      }),
      projectiles: structuredClone(this._state.projectiles.filter((projectile) => !projectile.dead)),
      result: this._state.result ? structuredClone(this._state.result) : null,
    };
  }

  snapshot(): BattleState {
    return structuredClone(this._state);
  }

  checksum(): string {
    const { arenaId: _presentationArenaId, ...simulationState } = this._state;
    return checksum({ state: simulationState, geometryId: this.arena.gameplay.geometryId, rng: this.rng.snapshot(), engineVersion: ENGINE_VERSION, contentVersion: this.content.version });
  }

  private validateCommand(command: BattleCommand): CommandRejectReason | null {
    if (this._state.result) return 'battle-ended';
    if (command.tick < this._state.tick) return 'late-command';
    if (command.tick > this._state.tick + 3) return 'future-command';
    const player = this._state.players[command.playerId];
    if (command.sequence !== player.nextSequence) return 'invalid-sequence';
    if (this.commands.some((pending) => pending.playerId === command.playerId)) return 'invalid-sequence';
    const card = this.content.cards.get(command.cardId);
    if (!card) return 'unknown-card';
    if (!player.hand.includes(command.cardId)) return 'card-not-in-hand';
    if (player.elixirMilli < card.cost * 1_000) return 'insufficient-elixir';
    if (command.tick < player.nextCardReadyTick) return 'card-cooldown';
    if (!this.isPlacementValid(command.playerId, card, command.position)) return 'invalid-position';
    return null;
  }

  private isPlacementValid(playerId: PlayerId, card: CardDefinition, position: WorldPoint): boolean {
    return isCardPlacementValid({ playerId, card, position, content: this.content, arena: this.arena.gameplay, entities: this._state.entities });
  }

  private applyDueCommands(): void {
    while (this.commands.length && this.commands[0]!.tick <= this._state.tick) {
      const command = this.commands.shift()!;
      const card = this.content.cards.get(command.cardId)!;
      const player = this._state.players[command.playerId];
      const handIndex = player.hand.indexOf(command.cardId);
      player.elixirMilli -= card.cost * 1_000;
      player.nextCardReadyTick = this._state.tick + cardCooldownAt(this.rules, this._state.phase, this._state.phaseTick);
      const cycled = player.queue.shift()!;
      player.queue.push(command.cardId);
      player.hand[handIndex] = cycled;
      this.events.push({ type: 'card-played', tick: this._state.tick, playerId: command.playerId, cardId: card.id, at: command.position });
      this.record('simulation', 'CARD_PLAYED', { playerId: command.playerId, position: command.position, cost: card.cost, elixirAfter: player.elixirMilli }, { commandSequence: command.sequence, cardIds: [card.id] });
      for (const mechanic of card.mechanics) {
        if (mechanic.type === 'spawn-units') this.spawnCardEntities(command.playerId, card, mechanic.groups, command.position);
        else if (mechanic.type === 'area-projectile') this.castSpell(command.playerId, card, mechanic.spellId, command.position);
        else {
          const exhaustive: never = mechanic;
          throw new Error(`Unsupported card mechanic: ${String(exhaustive)}`);
        }
      }
    }
  }

  private spawnCardEntities(owner: PlayerId, card: CardDefinition, _groups: readonly SpawnDefinition[], at: WorldPoint): void {
    const zone = card.placement === 'anywhere'
      ? undefined
      : deploymentZoneForPoint(activeDeploymentZones(this.arena.gameplay, owner, this._state.entities), at);
    for (const group of planSpawnGroups(owner, card, at, this.content, this.arena.gameplay, zone)) {
      if (group.positions.some((position, index) => position.x !== group.requestedPositions[index]!.x || position.y !== group.requestedPositions[index]!.y)) {
        this.record('simulation', 'FORMATION_ADJUSTED', { owner, unitId: group.definition.id, requestedAt: at, before: group.requestedPositions, after: group.positions, reason: 'fit-formation-inside-deployment-zone' }, { cardIds: [card.id], level: 'debug' });
      }
      for (let index = 0; index < group.positions.length; index += 1) {
        this.createEntity(owner, card.id, group.definition.movement === 'static' ? 'building' : 'troop', group.definition.id, group.positions[index]!, card.deployTimeTicks + index * group.staggerTicks);
      }
    }
  }

  private castSpell(owner: PlayerId, card: CardDefinition, spellId: string, at: WorldPoint): void {
    const spell = this.content.spells.get(spellId);
    if (!spell) throw new Error(`Unknown spell ${spellId} for card ${card.id}`);
    const lineBehavior = spell.behavior?.type === 'line-damage' ? spell.behavior : null;
    const isLine = lineBehavior !== null;
    const origin = isLine ? { ...at } : projectileOrigin(this.arena.gameplay, owner);
    const targetPoint = isLine
      ? clampPoint({ x: at.x, y: at.y + (owner === 'player' ? -lineBehavior.distance : lineBehavior.distance) }, this.arena.gameplay.worldBounds)
      : { ...at };
    const projectile: ProjectileState = {
      id: this._state.nextEntityId++, owner, sourceId: null, sourceCardId: card.id, targetId: null,
      targetPoint, position: origin, speedPerTick: lineBehavior?.speedPerTick ?? 0, damage: spell.damage, splashRadius: spell.radius,
      targetMask: 'all-enemies', towerDamageMultiplier: spell.towerDamageMultiplier, remainingTicks: spell.travelTicks,
      visualKey: spell.visualKey, dead: false,
      behavior: spell.behavior?.type ?? 'damage',
      ...(spell.behavior?.type === 'spawn-units' ? { spawnGroups: structuredClone(spell.behavior.groups) } : {}),
      ...(spell.behavior?.type === 'line-damage' ? { hitEntityIds: [], knockback: spell.behavior.knockback, remainingTicks: null } : {}),
    };
    this._state.projectiles.push(projectile);
    this.events.push({ type: 'projectile-spawned', tick: this._state.tick, projectileId: projectile.id, sourceId: null, visualKey: projectile.visualKey });
    this.record('simulation', 'PROJECTILE_SPAWNED', { projectileId: projectile.id, targetPoint: at, spellId: spell.id }, { entityIds: [projectile.id], cardIds: [card.id] });
  }

  private createEntity(
    owner: PlayerId,
    cardId: CardId,
    kind: EntityState['kind'],
    definitionId: string,
    position: WorldPoint,
    deployTicks: number,
    arenaSocketId: EntityState['arenaSocketId'] = null,
    inheritedRoute?: GroundRouteState,
  ): EntityState {
    const definition = this.content.units.get(definitionId)!;
    const entity: EntityState = {
      id: this._state.nextEntityId++, owner, kind, definitionId, sourceCardId: cardId, arenaSocketId, position: { ...position }, movement: definition.movement,
      radius: definition.radius, hp: definition.hp, maxHp: definition.hp, targetMask: definition.targetMask,
      ...(definition.weapon ? { weapon: structuredClone(definition.weapon) } : {}), speedPerTick: definition.speedPerTick,
      deployRemaining: deployTicks, cooldownRemaining: 0, windupRemaining: 0, pendingTargetId: null, targetId: null,
      route: inheritedRoute ? structuredClone(inheritedRoute) : createGroundRouteState(this._state.tick),
      lifetimeRemaining: definition.lifetimeTicks ?? null, active: kind !== 'tower' || definitionId !== 'king-tower', dead: false, removed: false, visualKey: definition.visualKey,
      ...(definition.deployEffect ? { deployEffect: structuredClone(definition.deployEffect) } : {}), deployEffectApplied: !definition.deployEffect,
      ...(definition.deathEffect ? { deathEffect: structuredClone(definition.deathEffect) } : {}),
      ...(definition.periodicSpawn ? { periodicSpawn: structuredClone(definition.periodicSpawn) } : {}), periodicSpawnRemaining: definition.periodicSpawn?.initialDelayTicks ?? null,
      ...(definition.leapAttack ? { leapAttack: structuredClone(definition.leapAttack) } : {}), leapCooldownRemaining: 0, leapState: null,
    };
    this._state.entities.push(entity);
    this.events.push({ type: 'entity-spawned', tick: this._state.tick, entityId: entity.id, owner, cardId, visualKey: entity.visualKey });
    this.record('simulation', 'ENTITY_SPAWNED', { owner, kind, definitionId, position, hp: entity.hp, movement: entity.movement, radius: entity.radius }, { entityIds: [entity.id], cardIds: [cardId] });
    return entity;
  }

  private spawnInitialTowers(): void {
    const sockets = this.arena.gameplay.towerSockets;
    const towerCard = 'tower' as CardId;
    this.createEntity('player', towerCard, 'tower', 'princess-tower-left', sockets.playerLeft, 0, 'playerLeft');
    this.createEntity('player', towerCard, 'tower', 'princess-tower-right', sockets.playerRight, 0, 'playerRight');
    this.createEntity('player', towerCard, 'tower', 'king-tower', sockets.playerKing, 0, 'playerKing');
    this.createEntity('bot', towerCard, 'tower', 'princess-tower-left', sockets.botLeft, 0, 'botLeft');
    this.createEntity('bot', towerCard, 'tower', 'princess-tower-right', sockets.botRight, 0, 'botRight');
    this.createEntity('bot', towerCard, 'tower', 'king-tower', sockets.botKing, 0, 'botKing');
  }

  private updateClockAndElixir(): void {
    if (this._state.phase === 'countdown' && this._state.phaseTick >= this.rules.countdownTicks) this.changePhase('normal');
    else if (this._state.phase === 'normal' && this._state.phaseTick >= this.rules.normalTicks) this.finishNormalTime();
    else if (this._state.phase === 'overtime' && this._state.phaseTick >= this.rules.overtimeTicks) this.finishAfterOvertime();
    if (this._state.phase === 'normal' || this._state.phase === 'overtime') {
      const stage = elixirStageAt(this.rules, this._state.phase, this._state.phaseTick);
      const rate = elixirRateAt(this.rules, this._state.phase, this._state.phaseTick);
      if (stage !== this.lastElixirStage) {
        this.lastElixirStage = stage;
        this.record('simulation', 'ELIXIR_RATE_CHANGED', { stage, rateMilliPerTick: rate });
      }
      for (const player of Object.values(this._state.players)) player.elixirMilli = Math.min(this.rules.maxElixirMilli, player.elixirMilli + rate);
    }
  }

  private finishNormalTime(): void {
    const playerCrowns = this._state.players.player.crowns;
    const botCrowns = this._state.players.bot.crowns;
    if (playerCrowns !== botCrowns) {
      this.finish(playerCrowns > botCrowns ? 'player' : 'bot', 'time');
      return;
    }
    if (this.rules.overtimeTicks > 0) this.changePhase('overtime');
    else this.finishTiebreak();
  }

  private changePhase(phase: BattleState['phase']): void {
    this._state.phase = phase;
    this._state.phaseTick = 0;
    this.events.push({ type: 'phase-changed', tick: this._state.tick, phase });
    this.record('simulation', 'PHASE_CHANGED', { phase });
  }

  private updateEntities(): void {
    const bounds = this.arena.gameplay.worldBounds;
    this.spatialIndex = new SpatialIndex(this._state.entities.filter((entity) => !entity.removed), Math.max(1, Math.min(bounds.width, bounds.height) / 8));
    for (const entity of this._state.entities.sort((left, right) => left.id - right.id)) {
      if (entity.dead || entity.removed) continue;
      if (entity.deployRemaining > 0) {
        entity.deployRemaining -= 1;
        if (entity.deployRemaining === 0) this.applyDeployEffect(entity);
        continue;
      }
      if (entity.lifetimeRemaining !== null) {
        entity.lifetimeRemaining -= 1;
        if (entity.lifetimeRemaining <= 0) {
          entity.hp = 0;
          continue;
        }
      }
      if (entity.windupRemaining > 0) {
        entity.windupRemaining -= 1;
        entity.cooldownRemaining = Math.max(0, entity.cooldownRemaining - 1);
        if (entity.windupRemaining === 0) this.releaseAttack(entity);
        continue;
      }
      if (entity.leapState) {
        this.updateLeap(entity);
        continue;
      }
      entity.cooldownRemaining = Math.max(0, entity.cooldownRemaining - 1);
      entity.leapCooldownRemaining = Math.max(0, entity.leapCooldownRemaining - 1);
      this.updatePeriodicSpawn(entity);
      if (entity.kind === 'tower' && !entity.active) continue;
      const target = acquireTarget(entity, this._state.entities, this.arena.gameplay, this.spatialIndex);
      const previousTargetId = entity.targetId;
      entity.targetId = target?.id ?? null;
      if (previousTargetId !== entity.targetId) {
        this.events.push({ type: 'target-changed', tick: this._state.tick, entityId: entity.id, fromId: previousTargetId, toId: entity.targetId, reason: 'nearest-valid' });
        this.record('simulation', 'TARGET_CHANGED', { fromId: previousTargetId, toId: entity.targetId, reason: 'nearest-valid' }, { entityIds: [entity.id, ...(target ? [target.id] : [])] });
      }
      if (!target) continue;
      const weapon = entity.weapon;
      const range = weapon?.range ?? 0;
      const currentDistance = distance(entity.position, target.position) - entity.radius - target.radius;
      if (entity.leapAttack && entity.leapCooldownRemaining <= 0 && currentDistance >= entity.leapAttack.minRange && currentDistance <= entity.leapAttack.maxRange) {
        this.startLeap(entity, target);
        continue;
      }
      if (weapon && currentDistance <= range) {
        if (entity.cooldownRemaining <= 0) this.startAttack(entity, target);
      } else if (entity.movement !== 'static') {
        this.moveToward(entity, target.position);
      }
    }
    this.spatialIndex = null;
  }

  private applyDeployEffect(entity: EntityState): void {
    if (!entity.deployEffect || entity.deployEffectApplied) return;
    entity.deployEffectApplied = true;
    this.triggerAreaEffect(entity, entity.deployEffect, 'deploy-damage');
  }

  private updatePeriodicSpawn(entity: EntityState): void {
    if (!entity.periodicSpawn || entity.periodicSpawnRemaining === null) return;
    entity.periodicSpawnRemaining -= 1;
    if (entity.periodicSpawnRemaining > 0) return;
    const effect = entity.periodicSpawn;
    this.emitMechanic(entity.id, entity.sourceCardId, 'periodic-spawn', 'start', entity.visualKey, entity.position);
    this.spawnDefinitionsAt(entity.owner, entity.sourceCardId, effect.groups, entity.position, 0, entity);
    this.emitMechanic(entity.id, entity.sourceCardId, 'periodic-spawn', 'impact', entity.visualKey, entity.position);
    this.emitMechanic(entity.id, entity.sourceCardId, 'periodic-spawn', 'complete', entity.visualKey, entity.position);
    entity.periodicSpawnRemaining = effect.intervalTicks;
  }

  private startLeap(entity: EntityState, target: EntityState): void {
    const leap = entity.leapAttack!;
    entity.leapState = { targetId: target.id, start: { ...entity.position }, end: { ...target.position }, remainingTicks: leap.durationTicks, totalTicks: leap.durationTicks };
    entity.targetId = target.id;
    this.emitMechanic(entity.id, entity.sourceCardId, 'leap', 'start', leap.visualKey, entity.position);
  }

  private updateLeap(entity: EntityState): void {
    const state = entity.leapState;
    const leap = entity.leapAttack;
    if (!state || !leap) return;
    state.remainingTicks -= 1;
    const progress = 1 - Math.max(0, state.remainingTicks) / state.totalTicks;
    entity.position.x = Math.round(state.start.x + (state.end.x - state.start.x) * progress);
    entity.position.y = Math.round(state.start.y + (state.end.y - state.start.y) * progress);
    if (state.remainingTicks > 0) return;
    entity.leapState = null;
    entity.leapCooldownRemaining = leap.cooldownTicks;
    this.applyAreaDamage(entity.id, entity.owner, entity.sourceCardId, leap.visualKey, entity.position, leap.damage, leap.radius, leap.towerDamageMultiplier);
    this.emitMechanic(entity.id, entity.sourceCardId, 'leap', 'impact', leap.visualKey, entity.position);
    this.emitMechanic(entity.id, entity.sourceCardId, 'leap', 'complete', leap.visualKey, entity.position);
  }

  private triggerAreaEffect(entity: EntityState, effect: { damage: number; radius: number; towerDamageMultiplier: number; visualKey: string }, mechanic: 'deploy-damage' | 'leap'): void {
    this.emitMechanic(entity.id, entity.sourceCardId, mechanic, 'start', effect.visualKey, entity.position);
    this.applyAreaDamage(entity.id, entity.owner, entity.sourceCardId, effect.visualKey, entity.position, effect.damage, effect.radius, effect.towerDamageMultiplier);
    this.emitMechanic(entity.id, entity.sourceCardId, mechanic, 'impact', effect.visualKey, entity.position);
    this.emitMechanic(entity.id, entity.sourceCardId, mechanic, 'complete', effect.visualKey, entity.position);
  }

  private emitMechanic(sourceId: EntityId | null, cardId: CardId, mechanic: 'deploy-damage' | 'death-damage' | 'periodic-spawn' | 'leap', phase: 'start' | 'impact' | 'complete' | 'cancel', visualKey: string, at: WorldPoint): void {
    this.events.push({ type: 'mechanic-triggered', tick: this._state.tick, sourceId, mechanic, phase, visualKey, at: { ...at } });
    this.record('simulation', phase === 'cancel' ? 'MECHANIC_CANCELLED' : `MECHANIC_${phase.toUpperCase()}`, { mechanic, visualKey, at }, { entityIds: sourceId === null ? [] : [sourceId], cardIds: [cardId] });
  }

  private spawnDefinitionsAt(owner: PlayerId, cardId: CardId, groups: readonly SpawnDefinition[], at: WorldPoint, deployTicks: number, source?: EntityState): void {
    for (const group of planSpawnDefinitions(groups, at, this.content, this.arena.gameplay, undefined, cardId)) {
      for (let index = 0; index < group.positions.length; index += 1) {
        const inheritedRoute = source?.movement === 'ground' && group.definition.movement === 'ground'
          ? {
              ...source.route,
              lastProgressTick: this._state.tick,
              lastWaypointDistance: null,
              noForwardProgressTicks: 0,
            }
          : undefined;
        const requested = group.positions[index]!;
        const resolved = resolveRuntimeSpawnPosition({
          position: requested,
          radius: group.definition.radius,
          movement: group.definition.movement,
          owner,
          arena: this.arena.gameplay,
          ...(inheritedRoute ? { inheritedRoute } : {}),
        });
        const entity = this.createEntity(
          owner,
          cardId,
          group.definition.movement === 'static' ? 'building' : 'troop',
          group.definition.id,
          resolved.position,
          deployTicks + index * group.staggerTicks,
          null,
          inheritedRoute,
        );
        if (resolved.adjusted) {
          this.record('simulation', 'RUNTIME_SPAWN_ADJUSTED', { sourceId: source?.id ?? null, requested, resolved: resolved.position, reason: resolved.reason, bridgeId: inheritedRoute?.bridgeId ?? null }, { entityIds: [entity.id, ...(source ? [source.id] : [])], cardIds: [cardId], level: 'debug' });
        }
      }
    }
  }

  private startAttack(source: EntityState, target: EntityState): void {
    const weapon = source.weapon!;
    source.pendingTargetId = target.id;
    source.windupRemaining = weapon.windupTicks;
    source.cooldownRemaining = weapon.cooldownTicks;
    this.events.push({ type: 'attack-started', tick: this._state.tick, sourceId: source.id, targetId: target.id, cardId: source.sourceCardId, releaseTick: this._state.tick + weapon.windupTicks });
    this.record('simulation', 'ATTACK_STARTED', { releaseTick: this._state.tick + weapon.windupTicks, distance: distance(source.position, target.position) }, { entityIds: [source.id, target.id], cardIds: [source.sourceCardId] });
    if (weapon.windupTicks === 0) this.releaseAttack(source);
  }

  private releaseAttack(source: EntityState): void {
    const target = this.entityById(source.pendingTargetId);
    const weapon = source.weapon;
    source.pendingTargetId = null;
    if (!target || target.dead || !weapon || !isValidTarget(source, target)) {
      this.events.push({ type: 'attack-cancelled', tick: this._state.tick, sourceId: source.id, targetId: target?.id ?? null, reason: 'target-invalid-at-release' });
      this.record('simulation', 'ATTACK_CANCELLED', { reason: 'target-invalid-at-release' }, { entityIds: [source.id, ...(target ? [target.id] : [])], cardIds: [source.sourceCardId] });
      return;
    }
    this.record('simulation', 'ATTACK_RELEASED', { targetId: target.id }, { entityIds: [source.id, target.id], cardIds: [source.sourceCardId] });
    if (weapon.projectileSpeed) {
      const projectile: ProjectileState = {
        id: this._state.nextEntityId++, owner: source.owner, sourceId: source.id, sourceCardId: source.sourceCardId, targetId: target.id,
        targetPoint: { ...target.position }, position: { ...source.position }, speedPerTick: weapon.projectileSpeed, damage: weapon.damage,
        splashRadius: weapon.splashRadius ?? 0, targetMask: 'all-enemies', towerDamageMultiplier: weapon.towerDamageMultiplier ?? 1,
        remainingTicks: null, visualKey: `${source.visualKey}.projectile`, dead: false,
      };
      this._state.projectiles.push(projectile);
      this.events.push({ type: 'projectile-spawned', tick: this._state.tick, projectileId: projectile.id, sourceId: source.id, visualKey: projectile.visualKey });
      this.record('simulation', 'PROJECTILE_SPAWNED', { projectileId: projectile.id, targetId: target.id, damage: weapon.damage }, { entityIds: [source.id, target.id, projectile.id], cardIds: [source.sourceCardId] });
    } else {
      this.applyDamage(source.id, source.owner, source.sourceCardId, source.visualKey, target, weapon.damage, 1);
    }
  }

  private cancelPendingAttack(source: EntityState, reason: 'source-died' | 'match-ended'): void {
    if (source.pendingTargetId === null) return;
    const targetId = source.pendingTargetId;
    source.pendingTargetId = null;
    source.windupRemaining = 0;
    this.events.push({ type: 'attack-cancelled', tick: this._state.tick, sourceId: source.id, targetId, reason });
    this.record('simulation', 'ATTACK_CANCELLED', { reason }, { entityIds: [source.id, targetId], cardIds: [source.sourceCardId] });
  }

  private cancelLeap(source: EntityState): void {
    if (!source.leapState || !source.leapAttack) return;
    source.leapState = null;
    this.emitMechanic(source.id, source.sourceCardId, 'leap', 'cancel', source.leapAttack.visualKey, source.position);
  }

  private moveToward(entity: EntityState, target: WorldPoint): void {
    const plan = routePlan(entity, target, this.arena.gameplay, this._state.entities, entity.targetId ?? undefined, this._state.tick);
    entity.route = plan.route;
    this.emitRouteTransition(entity, plan);
    const waypoint = plan.waypoint;
    const dx = waypoint.x - entity.position.x;
    const dy = waypoint.y - entity.position.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0) {
      this.updatePathProgress(entity, waypoint);
      return;
    }
    const amount = Math.min(entity.speedPerTick, length);
    const previous = { ...entity.position };
    const proposed = clampPoint({
      x: entity.position.x + Math.trunc((dx / length) * amount),
      y: entity.position.y + Math.trunc((dy / length) * amount),
    }, this.arena.gameplay.worldBounds, 200);
    const resolution = constrainGroundStep(entity, previous, proposed, this.arena.gameplay, entity.speedPerTick);
    entity.position = resolution.position;
    this.recordMovementResolution(entity, previous, proposed, resolution, 'movement');
    this.spatialIndex?.update(entity, previous);
    this.updatePathProgress(entity, waypoint);
  }

  private updatePathProgress(entity: EntityState, waypoint: WorldPoint): void {
    const waypointDistance = distance(entity.position, waypoint);
    const previousDistance = entity.route.lastWaypointDistance;
    const progressed = previousDistance === null || waypointDistance < previousDistance - 0.5;
    entity.route = {
      ...entity.route,
      lastWaypointDistance: waypointDistance,
      lastProgressTick: progressed ? this._state.tick : entity.route.lastProgressTick,
      noForwardProgressTicks: progressed ? 0 : entity.route.noForwardProgressTicks + 1,
    };
  }

  private emitRouteTransition(entity: EntityState, plan: NavigationPlan): void {
    const transition = plan.transition;
    if (!transition) return;
    if (transition.type === 'selected') {
      this.events.push({ type: 'route-changed', tick: this._state.tick, entityId: entity.id, bridgeId: transition.bridgeId });
      this.record('simulation', 'ROUTE_CHANGED', { bridgeId: transition.bridgeId, phase: entity.route.phase, reason: 'selected' }, { entityIds: [entity.id] });
      return;
    }
    if (transition.type === 'committed') {
      this.events.push({ type: 'route-committed', tick: this._state.tick, entityId: entity.id, bridgeId: transition.bridgeId });
      this.record('simulation', 'ROUTE_COMMITTED', { bridgeId: transition.bridgeId }, { entityIds: [entity.id] });
      return;
    }
    if (transition.type === 'crossing') {
      this.record('simulation', 'ROUTE_CROSSING', { bridgeId: transition.bridgeId }, { entityIds: [entity.id], level: 'debug' });
      return;
    }
    if (transition.type === 'released') {
      this.events.push({ type: 'route-released', tick: this._state.tick, entityId: entity.id, bridgeId: transition.bridgeId });
      this.events.push({ type: 'route-changed', tick: this._state.tick, entityId: entity.id, bridgeId: null });
      this.record('simulation', 'ROUTE_RELEASED', { bridgeId: transition.bridgeId }, { entityIds: [entity.id] });
      this.record('simulation', 'ROUTE_CHANGED', { bridgeId: null, reason: 'released' }, { entityIds: [entity.id], level: 'debug' });
      return;
    }
    this.events.push({ type: 'path-replanned', tick: this._state.tick, entityId: entity.id, reason: transition.reason, bridgeId: transition.bridgeId, replanCount: entity.route.replanCount });
    this.events.push({ type: 'route-changed', tick: this._state.tick, entityId: entity.id, bridgeId: transition.bridgeId });
    this.record('simulation', 'PATH_REPLANNED', { reason: transition.reason, fromBridgeId: transition.fromBridgeId, bridgeId: transition.bridgeId, replanCount: entity.route.replanCount }, { entityIds: [entity.id], level: 'warn' });
    this.record('simulation', 'ROUTE_CHANGED', { bridgeId: transition.bridgeId, fromBridgeId: transition.fromBridgeId, reason: transition.reason }, { entityIds: [entity.id] });
  }

  private recordMovementResolution(
    entity: EntityState,
    previous: WorldPoint,
    proposed: WorldPoint,
    resolution: MovementResolution,
    source: 'movement' | 'separation' | 'knockback',
  ): void {
    if (!resolution.constrained || resolution.reason === 'none') return;
    const payload = { source, previous, proposed, resolved: resolution.position, reason: resolution.reason, bridgeId: entity.route.bridgeId };
    if (resolution.rejected) this.record('simulation', 'NAVIGATION_STEP_REJECTED', payload, { entityIds: [entity.id], level: 'warn' });
    else this.record('simulation', source === 'separation' ? 'GROUND_SEPARATION_CLAMPED' : 'GROUND_ROUTE_CONSTRAINED', payload, { entityIds: [entity.id], level: 'debug' });
  }

  private resolveSeparation(): void {
    for (const proposal of computeFriendlyGroundSeparation(this._state.entities)) {
      const entity = this.entityById(proposal.entityId);
      if (!entity) continue;
      const previous = { ...entity.position };
      const proposed = clampPoint({
        x: previous.x + Math.trunc(proposal.delta.x),
        y: previous.y + Math.trunc(proposal.delta.y),
      }, this.arena.gameplay.worldBounds, entity.radius);
      const resolution = constrainGroundStep(entity, previous, proposed, this.arena.gameplay, SEPARATION_BUDGET);
      entity.position = resolution.position;
      this.recordMovementResolution(entity, previous, proposed, resolution, 'separation');
    }
  }

  private updateProjectiles(): void {
    for (const projectile of this._state.projectiles.sort((left, right) => left.id - right.id)) {
      if (projectile.dead) continue;
      if (projectile.behavior === 'line-damage') {
        this.updateLineProjectile(projectile);
        continue;
      }
      if (projectile.remainingTicks !== null) {
        projectile.remainingTicks -= 1;
        const total = Math.max(1, projectile.remainingTicks + 1);
        const next = clampPoint({ x: projectile.position.x + (projectile.targetPoint.x - projectile.position.x) / total, y: projectile.position.y + (projectile.targetPoint.y - projectile.position.y) / total }, this.arena.gameplay.worldBounds);
        projectile.position.x = next.x;
        projectile.position.y = next.y;
        if (projectile.remainingTicks <= 0) this.impactProjectile(projectile);
        continue;
      }
      const target = this.entityById(projectile.targetId);
      if (target && !target.dead) projectile.targetPoint = { ...target.position };
      const dx = projectile.targetPoint.x - projectile.position.x;
      const dy = projectile.targetPoint.y - projectile.position.y;
      const length = Math.hypot(dx, dy);
      if (length <= projectile.speedPerTick) this.impactProjectile(projectile);
      else {
        projectile.position.x += Math.trunc((dx / length) * projectile.speedPerTick);
        projectile.position.y += Math.trunc((dy / length) * projectile.speedPerTick);
      }
    }
  }

  private impactProjectile(projectile: ProjectileState): void {
    projectile.dead = true;
    this.events.push({ type: 'projectile-impacted', tick: this._state.tick, projectileId: projectile.id, visualKey: projectile.visualKey, at: { ...projectile.targetPoint } });
    if (projectile.mechanic === 'death-damage') this.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'impact', projectile.visualKey, projectile.targetPoint);
    if (projectile.behavior === 'line-damage') {
      this.record('simulation', 'PROJECTILE_IMPACT', { position: projectile.targetPoint, targetIds: projectile.hitEntityIds ?? [] }, { entityIds: [projectile.id, ...(projectile.hitEntityIds ?? [])], cardIds: [projectile.sourceCardId] });
      return;
    }
    if (projectile.behavior === 'spawn-units') {
      this.spawnDefinitionsAt(projectile.owner, projectile.sourceCardId, projectile.spawnGroups ?? [], projectile.targetPoint, 0);
      this.record('simulation', 'PROJECTILE_IMPACT', { position: projectile.targetPoint, spawnedGroups: projectile.spawnGroups ?? [] }, { entityIds: [projectile.id], cardIds: [projectile.sourceCardId] });
      return;
    }
    const targets = this._state.entities
      .filter((entity) => entity.owner !== projectile.owner && !entity.dead && !entity.removed && entity.deployRemaining <= 0)
      .filter((entity) => projectile.splashRadius > 0 ? distance(entity.position, projectile.targetPoint) <= projectile.splashRadius + entity.radius : entity.id === projectile.targetId)
      .sort((left, right) => left.id - right.id);
    for (const target of targets) this.applyDamage(projectile.sourceId, projectile.owner, projectile.sourceCardId, projectile.visualKey, target, projectile.damage, projectile.towerDamageMultiplier);
    this.record('simulation', 'PROJECTILE_IMPACT', { position: projectile.targetPoint, targetIds: targets.map((target) => target.id) }, { entityIds: [projectile.id, ...targets.map((target) => target.id)], cardIds: [projectile.sourceCardId] });
    if (projectile.mechanic === 'death-damage') this.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'complete', projectile.visualKey, projectile.targetPoint);
  }

  private updateLineProjectile(projectile: ProjectileState): void {
    const dx = projectile.targetPoint.x - projectile.position.x;
    const dy = projectile.targetPoint.y - projectile.position.y;
    const length = Math.hypot(dx, dy);
    const amount = Math.min(projectile.speedPerTick, length);
    if (length > 0) {
      projectile.position.x += Math.round((dx / length) * amount);
      projectile.position.y += Math.round((dy / length) * amount);
    }
    const alreadyHit = new Set(projectile.hitEntityIds ?? []);
    const targets = this._state.entities
      .filter((entity) => entity.owner !== projectile.owner && !entity.dead && !entity.removed && entity.deployRemaining <= 0 && entity.movement !== 'air' && !alreadyHit.has(entity.id))
      .filter((entity) => distance(entity.position, projectile.position) <= projectile.splashRadius + entity.radius)
      .sort((left, right) => left.id - right.id);
    for (const target of targets) {
      projectile.hitEntityIds?.push(target.id);
      this.applyDamage(projectile.sourceId, projectile.owner, projectile.sourceCardId, projectile.visualKey, target, projectile.damage, projectile.towerDamageMultiplier);
      if ((projectile.knockback ?? 0) > 0 && target.kind !== 'tower' && target.movement === 'ground') {
        const direction = projectile.owner === 'player' ? -1 : 1;
        const previous = { ...target.position };
        const proposed = clampPoint({ x: previous.x, y: previous.y + direction * (projectile.knockback ?? 0) }, this.arena.gameplay.worldBounds, target.radius);
        const resolution = constrainGroundStep(target, previous, proposed, this.arena.gameplay, projectile.knockback ?? 0);
        target.position = resolution.position;
        this.recordMovementResolution(target, previous, proposed, resolution, 'knockback');
      }
    }
    if (targets.length) this.record('simulation', 'LINE_PROJECTILE_HIT', { projectileId: projectile.id, targetIds: targets.map((target) => target.id) }, { entityIds: [projectile.id, ...targets.map((target) => target.id)], cardIds: [projectile.sourceCardId] });
    if (length <= projectile.speedPerTick) this.impactProjectile(projectile);
  }

  private applyAreaDamage(sourceId: EntityId | null, owner: PlayerId, cardId: CardId, visualKey: string, at: WorldPoint, damage: number, radius: number, towerDamageMultiplier: number): void {
    const targets = this._state.entities
      .filter((entity) => entity.owner !== owner && !entity.dead && !entity.removed && entity.deployRemaining <= 0)
      .filter((entity) => distance(entity.position, at) <= radius + entity.radius)
      .sort((left, right) => left.id - right.id);
    for (const target of targets) this.applyDamage(sourceId, owner, cardId, visualKey, target, damage, towerDamageMultiplier);
  }

  private applyDamage(sourceId: EntityId | null, sourceOwner: PlayerId, sourceCardId: CardId, visualKey: string, target: EntityState, damage: number, towerMultiplier: number): void {
    const amount = Math.max(0, Math.trunc(damage * (target.kind === 'tower' ? towerMultiplier : 1)));
    const hpBefore = target.hp;
    target.hp = Math.max(0, target.hp - amount);
    this.events.push({ type: 'damage-applied', tick: this._state.tick, sourceId, targetId: target.id, visualKey, amount, hpBefore, hpAfter: target.hp });
    if (target.kind === 'tower') this.events.push({ type: 'tower-hit', tick: this._state.tick, entityId: target.id, amount });
    this.record('simulation', 'DAMAGE_APPLIED', { sourceOwner, sourceCardId, amount, hpBefore, hpAfter: target.hp }, { entityIds: [...(sourceId === null ? [] : [sourceId]), target.id], cardIds: [sourceCardId] });
    if (target.kind === 'tower' && target.arenaSocketId === kingSocket(target.owner) && !target.active) {
      this.activateTower(target, 'direct-damage');
    }
  }

  private resolveDeathsAndResult(): void {
    const kingWinners = new Set<PlayerId>();
    for (const entity of this._state.entities.sort((left, right) => left.id - right.id)) {
      if (entity.dead || entity.removed || entity.hp > 0) continue;
      this.cancelPendingAttack(entity, 'source-died');
      this.cancelLeap(entity);
      entity.dead = true;
      entity.targetId = null;
      this.events.push({ type: 'entity-died', tick: this._state.tick, entityId: entity.id, owner: entity.owner });
      this.record('simulation', 'ENTITY_DIED', { owner: entity.owner, kind: entity.kind }, { entityIds: [entity.id], cardIds: [entity.sourceCardId] });
      if (entity.deathEffect) {
        const effect = entity.deathEffect;
        const projectile: ProjectileState = {
          id: this._state.nextEntityId++, owner: entity.owner, sourceId: entity.id, sourceCardId: entity.sourceCardId, targetId: null,
          targetPoint: { ...entity.position }, position: { ...entity.position }, speedPerTick: 0, damage: effect.damage, splashRadius: effect.radius,
          targetMask: 'all-enemies', towerDamageMultiplier: effect.towerDamageMultiplier, remainingTicks: Math.max(1, effect.delayTicks),
          visualKey: effect.visualKey, dead: false, behavior: 'damage', mechanic: 'death-damage',
        };
        this._state.projectiles.push(projectile);
        this.emitMechanic(entity.id, entity.sourceCardId, 'death-damage', 'start', effect.visualKey, entity.position);
        this.events.push({ type: 'projectile-spawned', tick: this._state.tick, projectileId: projectile.id, sourceId: entity.id, visualKey: projectile.visualKey });
        this.record('simulation', 'PROJECTILE_SPAWNED', { projectileId: projectile.id, mechanic: 'death-damage', delayTicks: effect.delayTicks }, { entityIds: [entity.id, projectile.id], cardIds: [entity.sourceCardId] });
      }
      if (entity.kind === 'tower') {
        const winner = otherPlayer(entity.owner);
        const kingDestroyed = entity.arenaSocketId === kingSocket(entity.owner);
        this._state.players[winner].crowns = kingDestroyed ? 3 : Math.min(2, this._state.players[winner].crowns + 1);
        this.events.push({ type: 'tower-destroyed', tick: this._state.tick, entityId: entity.id, crowns: this._state.players[winner].crowns });
        this.record('simulation', 'TOWER_DESTROYED', { winner, crowns: this._state.players[winner].crowns }, { entityIds: [entity.id] });
        if (entity.arenaSocketId) {
          const unlocked = this.arena.gameplay.deploymentZones.filter((zone) => zone.owner === winner && zone.unlockTowerSocketId === entity.arenaSocketId);
          for (const zone of unlocked) {
            this.events.push({ type: 'deployment-zone-unlocked', tick: this._state.tick, playerId: winner, zoneId: zone.id, laneId: zone.laneId, towerSocketId: entity.arenaSocketId });
            this.record('simulation', 'DEPLOYMENT_ZONE_UNLOCKED', { playerId: winner, zoneId: zone.id, laneId: zone.laneId, towerSocketId: entity.arenaSocketId }, { entityIds: [entity.id] });
          }
        }
        const king = this._state.entities.find((candidate) => candidate.arenaSocketId === kingSocket(entity.owner));
        if (!kingDestroyed && king && !king.active) this.activateTower(king, 'princess-destroyed');
        if (kingDestroyed) kingWinners.add(winner);
      }
    }
    if (kingWinners.size === 1) this.finish([...kingWinners][0]!, 'king-destroyed');
    else if (kingWinners.size > 1) this.finish('draw', 'king-destroyed');
    else if (this._state.phase === 'overtime' && this._state.players.player.crowns !== this._state.players.bot.crowns) {
      this.finish(this._state.players.player.crowns > this._state.players.bot.crowns ? 'player' : 'bot', 'sudden-death');
    }
  }

  private activateTower(tower: EntityState, reason: 'direct-damage' | 'princess-destroyed'): void {
    if (tower.active || tower.dead || tower.removed) return;
    tower.active = true;
    this.events.push({ type: 'tower-activated', tick: this._state.tick, entityId: tower.id });
    this.record('simulation', 'TOWER_ACTIVATED', { reason }, { entityIds: [tower.id], cardIds: [tower.sourceCardId] });
  }

  private finishAfterOvertime(): void {
    const playerCrowns = this._state.players.player.crowns;
    const botCrowns = this._state.players.bot.crowns;
    if (playerCrowns !== botCrowns) this.finish(playerCrowns > botCrowns ? 'player' : 'bot', 'time');
    else this.finishTiebreak();
  }

  private finishTiebreak(): void {
    const lowestTowerHp = (owner: PlayerId): number => Math.min(...this._state.entities
      .filter((entity) => entity.owner === owner && entity.kind === 'tower' && !entity.dead)
      .map((entity) => entity.hp));
    const playerHealth = lowestTowerHp('player');
    const botHealth = lowestTowerHp('bot');
    this.finish(playerHealth === botHealth ? 'draw' : playerHealth > botHealth ? 'player' : 'bot', 'tiebreak');
  }

  private finish(winner: PlayerId | 'draw', reason: 'king-destroyed' | 'time' | 'sudden-death' | 'tiebreak'): void {
    if (this._state.result) return;
    for (const entity of this._state.entities) {
      this.cancelPendingAttack(entity, 'match-ended');
      this.cancelLeap(entity);
    }
    for (const projectile of this._state.projectiles) {
      if (projectile.dead) continue;
      projectile.dead = true;
      if (projectile.mechanic === 'death-damage') this.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'cancel', projectile.visualKey, projectile.position);
      this.events.push({ type: 'projectile-cancelled', tick: this._state.tick, projectileId: projectile.id, sourceId: projectile.sourceId, visualKey: projectile.visualKey, reason: 'match-ended' });
      this.record('simulation', 'PROJECTILE_CANCELLED', { reason: 'match-ended', position: projectile.position, targetPoint: projectile.targetPoint }, { entityIds: [projectile.id, ...(projectile.sourceId === null ? [] : [projectile.sourceId])], cardIds: [projectile.sourceCardId] });
    }
    this._state.phase = 'ended';
    this._state.result = { winner, reason, crowns: { player: this._state.players.player.crowns, bot: this._state.players.bot.crowns }, tick: this._state.tick };
    this.replay.result = structuredClone(this._state.result);
    this.events.push({ type: 'battle-ended', tick: this._state.tick, result: structuredClone(this._state.result) });
    this.record('simulation', 'BATTLE_ENDED', this._state.result);
  }

  private cleanup(): void {
    for (const entity of this._state.entities) if (entity.dead) entity.removed = true;
    this._state.entities = this._state.entities.filter((entity) => !entity.removed || entity.kind === 'tower');
    this._state.projectiles = this._state.projectiles.filter((projectile) => !projectile.dead);
  }

  private entityById(id: EntityId | null): EntityState | null {
    return id === null ? null : this._state.entities.find((entity) => entity.id === id && !entity.removed) ?? null;
  }

  private checkpoint(): void {
    const point = { tick: this._state.tick, checksum: this.checksum() };
    this.checkpoints.push(point);
    this.replay.checkpoints.push(point);
    this.record('simulation', 'CHECKPOINT', point, { level: 'debug' });
  }

  private recordStateSamples(): void {
    for (const player of Object.values(this._state.players)) {
      if (player.elixirMilli < 0 || player.elixirMilli > this.rules.maxElixirMilli) this.record('simulation', 'ELIXIR_OUT_OF_RANGE', { playerId: player.id, value: player.elixirMilli, max: this.rules.maxElixirMilli }, { level: 'error' });
    }
    for (const entity of this._state.entities) {
      if (entity.removed) continue;
      const values = [entity.position.x, entity.position.y, entity.hp];
      if (values.some((value) => !Number.isFinite(value))) this.record('simulation', 'STATE_NON_FINITE', { values }, { entityIds: [entity.id], level: 'error' });
      if (entity.hp < 0 || entity.hp > entity.maxHp) this.record('simulation', 'HP_OUT_OF_RANGE', { hp: entity.hp, maxHp: entity.maxHp }, { entityIds: [entity.id], level: 'error' });
      const bounds = this.arena.gameplay.worldBounds;
      if (entity.position.x < bounds.x || entity.position.x > bounds.x + bounds.width || entity.position.y < bounds.y || entity.position.y > bounds.y + bounds.height) this.record('simulation', 'ENTITY_OUT_OF_BOUNDS', { position: entity.position, bounds }, { entityIds: [entity.id], level: 'error' });
      const animation = entityAnimationState(entity, this.entityById(entity.targetId));
      const { moving, attacking } = animation;
      const sampleKey = `${entity.position.x}:${entity.position.y}:${entity.hp}:${entity.route.bridgeId ?? ''}:${entity.route.phase}:${moving ? 1 : 0}:${attacking ? 1 : 0}`;
      if (moving || attacking || this.lastStateSamples.get(entity.id) !== sampleKey) {
        this.record('simulation', 'ENTITY_POSITION', { x: entity.position.x, y: entity.position.y, movement: entity.movement, radius: entity.radius, bridgeId: entity.route.bridgeId, routePhase: entity.route.phase, moving, attacking }, { entityIds: [entity.id], level: 'debug' });
        this.lastStateSamples.set(entity.id, sampleKey);
      }
    }
  }

  private record(
    phase: 'command' | 'simulation' | 'bot' | 'presentation' | 'performance',
    code: string,
    payload: unknown,
    optional: { commandSequence?: number; entityIds?: readonly number[]; cardIds?: readonly CardId[]; level?: 'debug' | 'info' | 'warn' | 'error' } = {},
  ): void {
    this.journal.record({ tick: this._state.tick, phase, source: phase === 'simulation' ? 'engine' : phase, code, payload, ...optional });
  }
}
